import React, { useState } from 'react'
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '../common/page_component/index.js'
import Alert from '../common/Alert'
import tripContractService from '../../services/tripContractService'
import { ArrowLeft, Save } from 'lucide-react'
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import Button from '../common/Button.js'
import { useTranslation } from 'react-i18next';


const AddTripContract = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    contract_party_id: '',
    vehicle_id: '',
    delivery_location: '',
    base_trip_cost: '',
    handling: '',
    parking: '',
    other: '',
    remark: ''
  })
  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    contract_party_id: (v) => validators.select(v, 'contract party'),
    vehicle_id:        (v) => validators.select(v, 'vehicle'),
    delivery_location: (v) => validators.required(v, 'Delivery location'),
    base_trip_cost:    [(v) => validators.required(v, 'Base trip cost'), (v) => validators.number(v, 0)],
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    let filtered = value
    if (['base_trip_cost', 'handling', 'parking', 'other'].includes(name)) {
      filtered = inputFilters.amount(value)
    }
    setFormData({ ...formData, [name]: filtered })

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate(formData, validationRules)) return

    try {
      setLoading(true)
      const payload = {
        contract_party_id: formData.contract_party_id,
        vehicle_id: formData.vehicle_id,
        delivery_location: formData.delivery_location,
        base_trip_cost: parseFloat(formData.base_trip_cost),
        handling: formData.handling ? parseFloat(formData.handling) : 0,
        parking: formData.parking ? parseFloat(formData.parking) : 0,
        other: formData.other ? parseFloat(formData.other) : 0,
        remark: formData.remark
      }

      const response = await tripContractService.create(payload)
      Alert.success('Trip contract created successfully!')
      navigate(`/trip-contracts/view/${response.data.data.id}`)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create trip contract')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const calculateTotal = () => {
    const base = parseFloat(formData.base_trip_cost || 0)
    const handling = parseFloat(formData.handling || 0)
    const parking = parseFloat(formData.parking || 0)
    const other = parseFloat(formData.other || 0)
    return (base + handling + parking + other).toFixed(2)
  }

  const total = calculateTotal()

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        {/* Page Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/trip-contracts')} icon={ArrowLeft} />
          <div>
            <h1 className="text-2xl font-bold text-secondary">Add Trip Contract</h1>
            <p className="text-sm text-gray-600 mt-1">Create a new trip contract</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contract Party */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contract Party *
              </label>
              <SelectField variant="form"
                name="contract_party_id"
                value={formData.contract_party_id}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.contract_party_id ? 'border-red-500' : 'border-gray-200'
                }`}
              >
                <option value="">Select a contract party</option>
                <option value="party1">Party 1</option>
                <option value="party2">Party 2</option>
              </SelectField>
              <ValidationMessage error={errors.contract_party_id} />
            </div>

            {/* Vehicle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle *
              </label>
              <SelectField variant="form"
                name="vehicle_id"
                value={formData.vehicle_id}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.vehicle_id ? 'border-red-500' : 'border-gray-200'
                }`}
              >
                <option value="">Select a vehicle</option>
                <option value="vehicle1">Vehicle 1</option>
                <option value="vehicle2">Vehicle 2</option>
              </SelectField>
              <ValidationMessage error={errors.vehicle_id} />
            </div>
          </div>

          {/* Delivery Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Location *
            </label>
            <input
              type="text"
              name="delivery_location"
              value={formData.delivery_location}
              onChange={handleInputChange}
              placeholder="Enter delivery location"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                errors.delivery_location ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            <ValidationMessage error={errors.delivery_location} />
          </div>

          {/* Cost Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base Trip Cost *
              </label>
              <input
                type="text"
                name="base_trip_cost"
                value={formData.base_trip_cost}
                onChange={handleInputChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.base_trip_cost ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.base_trip_cost} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Handling (Optional)
              </label>
              <input
                type="text"
                name="handling"
                value={formData.handling}
                onChange={handleInputChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.handling ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.handling} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parking (Optional)
              </label>
              <input
                type="text"
                name="parking"
                value={formData.parking}
                onChange={handleInputChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.parking ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.parking} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Other Charges (Optional)
              </label>
              <input
                type="text"
                name="other"
                value={formData.other}
                onChange={handleInputChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.other ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.other} />
            </div>
          </div>

          {/* Total Cost Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center text-lg font-semibold">
              <span>Total Cost:</span>
              <span className="text-orange-600">
                {parseFloat(total).toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                })}
              </span>
            </div>
          </div>

          {/* Remark */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remarks (Optional)
            </label>
            <textarea
              name="remark"
              value={formData.remark}
              onChange={handleInputChange}
              placeholder="Add any additional remarks"
              rows="3"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-6">
            <Button variant="secondary" onClick={() => navigate('/trip-contracts')} className="flex-1">
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={loading} icon={Save} className="flex-1">
              {loading ? 'Creating...' : 'Create Trip Contract'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}

export default AddTripContract
